import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Button, Card, StatusBadge, TextInput, useLocale } from '@ministry/ui/internal';
import ScanBarcode from 'lucide-react/dist/esm/icons/scan-barcode.js';
import CameraOff from 'lucide-react/dist/esm/icons/camera-off.js';

import { completeAppointment, verifyAppointment, type AppointmentRow, type VerifyInput } from '../lib/appointments.js';
import { appointmentStatusEntry } from '../lib/appointment-lifecycle.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { formatInstantDisplay } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';

const REFERENCE_PATTERN = /^APT-[A-Z2-7]{10}$/;
const QR_PATTERN = /^[A-Z2-7]{32}$/;

/**
 * Minimal ambient typing for the native `BarcodeDetector` API (Chrome/Edge only — not yet
 * in TypeScript's own DOM lib). Feature-detected below; this app ships NO third-party
 * QR-decode library (`packages/ui` depends on `qrcode`, which only ENCODES — confirmed in
 * `package.json`, which this build may not edit to add a decoder). Where the browser lacks
 * it, the camera button is not rendered at all rather than shown disabled with no
 * explanation — manual reference entry is the fully-functional primary path either way
 * (SCREEN_SPECS.md I15: "budget-hardware realism applies here too").
 */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}
declare const BarcodeDetector:
  | (new (options?: { formats: string[] }) => BarcodeDetectorLike)
  | undefined;

function cameraScanSupported(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia) && typeof BarcodeDetector !== 'undefined';
}

/** SCREEN_SPECS.md I15 — entrance verification. Not tied to any single appointment: this is the tool a front-desk user opens repeatedly, once per visitor, all day. */
export function EntranceVerificationPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [code, setCode] = React.useState('');
  const [result, setResult] = React.useState<AppointmentRow | null>(null);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [scanning, setScanning] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const trimmed = code.trim().toUpperCase();
  const isReference = REFERENCE_PATTERN.test(trimmed);
  const isQr = QR_PATTERN.test(trimmed);
  const formatInvalid = trimmed !== '' && !isReference && !isQr;

  const stopScan = React.useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  }, []);

  React.useEffect(() => () => stopScan(), [stopScan]);

  const verifyMutation = useMutation({
    mutationFn: (input: VerifyInput) => verifyAppointment(input),
    onSuccess: (data) => {
      setResult(data.appointment);
      setCode('');
      stopScan();
    },
  });

  const completeMutation = useMutation({
    mutationFn: (appointment: AppointmentRow) => completeAppointment(appointment.id, appointment.version),
    onSuccess: (updated) => setResult(updated),
  });

  const submit = () => {
    if (isReference) verifyMutation.mutate({ referenceNo: trimmed });
    else if (isQr) verifyMutation.mutate({ qrToken: trimmed });
  };

  const startScan = async () => {
    setCameraError(null);
    verifyMutation.reset();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setScanning(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const detector = new (BarcodeDetector as NonNullable<typeof BarcodeDetector>)({ formats: ['qr_code'] });
      const tick = async () => {
        if (!videoRef.current || streamRef.current === null) return;
        try {
          const codes = await detector.detect(videoRef.current);
          const raw = codes[0]?.rawValue;
          if (raw) {
            const candidate = raw.trim().toUpperCase();
            if (REFERENCE_PATTERN.test(candidate) || QR_PATTERN.test(candidate)) {
              setCode(candidate);
              stopScan();
              verifyMutation.mutate(REFERENCE_PATTERN.test(candidate) ? { referenceNo: candidate } : { qrToken: candidate });
              return;
            }
          }
        } catch {
          // A single failed detect() call on one frame is not a camera failure — keep scanning.
        }
        rafRef.current = requestAnimationFrame(() => void tick());
      };
      rafRef.current = requestAnimationFrame(() => void tick());
    } catch {
      // SCREEN_SPECS.md I15: "Couldn't access the camera — enter the reference number
      // instead," which falls back to the manual entry field automatically — the field is
      // already the primary control on this screen, so "falling back" here means simply
      // surfacing why the camera didn't open and leaving focus where it already is.
      setCameraError(t('appointments.verify.camera.error'));
      stopScan();
    }
  };

  const error = verifyMutation.error;
  let errorBanner: React.ReactNode = null;
  if (error instanceof ApiError) {
    if (error.code === 'NOT_FOUND' || error.code === 'CONFLICT_STATE') {
      const currentStatus =
        error.code === 'CONFLICT_STATE' && typeof error.details?.currentStatus === 'string' ? error.details.currentStatus : null;
      errorBanner = (
        <AlertBanner variant="critical" title={t('appointments.verify.error.invalidOrUsed')}>
          {currentStatus ? t('appointments.verify.error.currentStatus', { status: currentStatus }) : null}
        </AlertBanner>
      );
    } else if (error.code === 'VALIDATION_FAILED') {
      errorBanner = <AlertBanner variant="critical" title={t('appointments.verify.error.format')} />;
    } else if (error.retryable) {
      errorBanner = <AlertBanner variant="critical" title={t('appointments.verify.error.network')} />;
    } else {
      errorBanner = <AlertBanner variant="critical" title={t('appointments.verify.error.generic')} />;
    }
  } else if (error instanceof NetworkError) {
    errorBanner = <AlertBanner variant="critical" title={t('appointments.verify.error.network')} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('appointments.verify.title')}
        description={t('appointments.verify.description')}
        breadcrumb={[
          { label: t('shell.dashboard'), href: ROUTES.dashboard },
          { label: t('appointments.inbox.title'), href: ROUTES.appointments },
          { label: t('appointments.verify.title') },
        ]}
      />

      <Card className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <TextInput
              id="entrance-verify-code"
              label={t('appointments.verify.codeLabel')}
              placeholder="APT-XXXXXXXXXX"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                verifyMutation.reset();
              }}
              errorText={formatInvalid ? t('appointments.verify.codeInvalid') : undefined}
              helperText={t('appointments.verify.codeHelper')}
              autoComplete="off"
            />
          </div>
          <Button onClick={submit} disabled={!isReference && !isQr} loading={verifyMutation.isPending} loadingLabel={t('appointments.verify.submitting')}>
            {t('appointments.verify.submit')}
          </Button>
        </div>

        {cameraScanSupported() ? (
          <div className="flex flex-col gap-2">
            {!scanning ? (
              <Button variant="secondary" size="sm" onClick={() => void startScan()}>
                <ScanBarcode aria-hidden="true" width={16} height={16} /> {t('appointments.verify.scanWithCamera')}
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                {/* A live camera viewfinder, not prerecorded media — captions do not apply. */}
                <video ref={videoRef} muted playsInline aria-label={t('appointments.verify.cameraPreviewLabel')} className="max-w-xs rounded-md border border-border-decorative" />
                <Button variant="ghost" size="sm" onClick={stopScan}>
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm text-text-muted">
            <CameraOff aria-hidden="true" width={16} height={16} />
            {t('appointments.verify.camera.unsupported')}
          </p>
        )}
        {cameraError ? <AlertBanner variant="warning" title={cameraError} /> : null}
      </Card>

      {errorBanner}

      {result ? (
        <Card className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-mono text-sm text-text-muted">{result.referenceNo}</div>
              <div className="text-lg font-semibold text-text-heading">{result.visitor.name}</div>
            </div>
            <StatusBadge entry={appointmentStatusEntry(result.status)} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.when')}</div>
              <div className="text-text-primary">{formatInstantDisplay(result.scheduledStart ?? result.requestedStart, locale)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-text-muted">{t('appointments.detail.purpose')}</div>
              <div className="text-text-primary">{result.purpose}</div>
            </div>
          </div>
          {result.status.code === 'arrived' ? (
            <div className="flex items-center gap-3">
              <Button onClick={() => completeMutation.mutate(result)} loading={completeMutation.isPending} loadingLabel={t('appointments.verify.completing')}>
                {t('appointments.verify.markCompleted')}
              </Button>
              {completeMutation.isError ? (
                <span className="text-sm text-status-critical-tint-fg">{t('appointments.verify.completeError')}</span>
              ) : null}
            </div>
          ) : result.status.code === 'completed' ? (
            <AlertBanner variant="info" title={t('appointments.verify.alreadyCompleted')} />
          ) : null}
          <Link to={ROUTES.appointmentDetail(result.id)} className="self-start text-sm font-semibold text-text-link underline">
            {t('appointments.verify.viewFullDetail')}
          </Link>
        </Card>
      ) : null}
    </div>
  );
}
